import { useMemo } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { useGetUser } from 'app/api/hooks/core/v1/auth/user';
import { useCanViewPaperwork } from 'app/api/hooks/paperwork/v1/bills';
import { payeeRoutes, routes } from 'app/constants/routes';
import { useAuthState, useCanManageUsers } from 'app/state/AuthContext/hooks';
import { useQueryString } from 'app/state/NavigationContext/hooks';
import { isPayeeAdmin, isPayeeAndOnlyDriver, isTriumphInternal } from 'app/utils/lib/user';

import {
  AddBankAccount,
  Broker,
  CreditNote,
  Dashboard,
  FourOhFour,
  Invoice,
  Invoices,
  InvoiceSuccess,
  NoRoleLandingPage,
  Paperwork,
  PaperworkCreate,
  PaperworkEdit,
  PaperworkShow,
  PaperworkSuccess,
  PayeeLayout,
  Payment,
  Payments,
  Profile,
  Relationship,
  RelationshipsSuccess,
  SelectCarrier,
  TermsOfServicePage,
  UnclaimedRelationship,
  UnclaimedRelationships,
  UserCreate,
  UserEdit,
} from './PayeeRouteComponents';

export const useGetPayeeRoutes = (): RouteObject[] => {
  const { authRoles } = useAuthState();
  const user = useGetUser();

  const paperworkQueryString = useQueryString('payeePaperwork');
  const canManageUsers = useCanManageUsers();

  const canViewPaperwork = useCanViewPaperwork();
  const userIsDriverOnly = isPayeeAndOnlyDriver(user);
  const showDriverWelcomePage = (userIsDriverOnly || !authRoles.length) && !canViewPaperwork;
  const isAdmin = isPayeeAdmin(user);
  const isInternal = isTriumphInternal(authRoles);
  const defaultPath = userIsDriverOnly
    ? `/${routes.payee}/${payeeRoutes.paperwork}${paperworkQueryString}`
    : `/${routes.payee}/${payeeRoutes.dashboard}`;

  return useMemo(() => [
    {
      children: [
        {
          element: (
            <Navigate
              replace
              to={defaultPath}
            />
          ),
          path: '',
        },
        ...(!userIsDriverOnly
          ? [
            {
              element: <Broker />,
              path: `${payeeRoutes.brokers}/:id`,
            },
            {
              element: <CreditNote />,
              path: `${payeeRoutes.creditNotes}/:id`,
            },
            {
              element: <Dashboard />,
              path: payeeRoutes.dashboard,
            },
            {
              element: <Invoice />,
              path: `${payeeRoutes.invoices}/:id`,
            },
            {
              element: <Invoices />,
              path: payeeRoutes.invoices,
            },
            {
              element: <InvoiceSuccess />,
              path: payeeRoutes.invoicesSuccess,
            },
            {
              element: <Payment />,
              path: `${payeeRoutes.payments}/:id`,
            },
            {
              element: <Payments />,
              path: payeeRoutes.payments,
            },
            {
              element: <AddBankAccount />,
              path: `${payeeRoutes.unclaimedRelationships}/add_bank_account`,
            },
            {
              element: <UnclaimedRelationship />,
              path: `${payeeRoutes.unclaimedRelationships}/:id`,
            },
            {
              element: <UnclaimedRelationships />,
              path: payeeRoutes.unclaimedRelationships,
            },
            {
              element: <TermsOfServicePage />,
              path: `${payeeRoutes.relationships}/:id/${payeeRoutes.relationshipsTermsOfService}`,
            },
            {
              element: <RelationshipsSuccess />,
              path: `${payeeRoutes.relationships}/success`,
            },
            {
              element: <Relationship />,
              path: `${payeeRoutes.relationships}/:id`,
            },
            {
              element: <SelectCarrier />,
              path: payeeRoutes.selectCarrier,
            },
          ]
          : []),
        ...((isAdmin || isInternal) && canManageUsers
          ? [
            {
              element: <UserCreate />,
              path: `${payeeRoutes.users}/new`,
            },
            {
              element: <UserEdit />,
              path: `${payeeRoutes.users}/:id/edit`,
            },
          ]
          : []
        ),
        {
          element: <PaperworkCreate />,
          path: `${payeeRoutes.paperwork}/new`,
        },
        {
          element: <PaperworkEdit />,
          path: `${payeeRoutes.paperwork}/:id/edit`,
        },
        {
          element: <PaperworkSuccess />,
          path: `${payeeRoutes.paperwork}/:id/success`,
        },
        {
          element: <PaperworkShow />,
          path: `${payeeRoutes.paperwork}/:id`,
        },
        {
          element: showDriverWelcomePage ? <NoRoleLandingPage /> : <Paperwork />,
          path: payeeRoutes.paperwork,
        },
        {
          element: <Profile />,
          path: payeeRoutes.profile,
        },
        {
          element: <FourOhFour />,
          path: '*',
        },
      ],
      element: <PayeeLayout />,
      path: '/payee',
    },
  ],
  [canManageUsers, defaultPath, isAdmin, isInternal, showDriverWelcomePage, userIsDriverOnly]);
};
